import { ReactElement, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Form } from '@weave/schema-gen-ts/dist/schemas/messaging/auto-rules/v1/models.pb';
import { TemplateType_Slug } from '@weave/schema-gen-ts/dist/schemas/messaging/templator/v2/model.pb';
import { useListBrandSettings } from '@frontend/api-brand-settings';
import { useAppScopeStore } from '@frontend/scope';
import {
  BaseFieldProps,
  contextFactory,
  DynamicFieldAttributes,
  SharedDynamicFieldsProps,
  useFormField,
} from '@frontend/design-system';
import { useActivePanel } from './hooks/use-active-panel';
import { useHtmlRenderer } from './hooks/use-html-renderer';
import { useInsertSection } from './hooks/use-insert-section';
import { useStateManagement } from './hooks/use-state-management';
import { useValidation } from './hooks/use-validation';
import {
  ComposerDocument,
  ComposerMode,
  ComposerPanel,
  ComposerParts,
  ValidatorErrorWithId,
  ValidatorErrorWithIndex,
} from './types';

type ContentComposerContextState = {
  activePanel: ComposerPanel;
  cancelRenderHtml: () => void;
  composerMode: ComposerMode;
  form?: Form;
  getValue: () => ComposerDocument | '';
  hasValidated: boolean;
  insertSection: (element: ReactElement, index?: number) => void;
  isValidating: boolean;
  locationIds: string[];
  parts: ComposerParts;
  promptProps: BaseFieldProps<string>; // TODO: Move this completely out of the content composer, but modify components to support it
  renderHtml: (mode?: string) => Promise<string | undefined>;
  scheduleSectionValidation: (sectionId: string) => void;
  selectedLocationId: string;
  setActivePanel: (panel: Exclude<ComposerPanel, 'settings'>) => void;
  clearSectionValidation: (sectionId: string) => void;
  setForm?: (form: Form) => void;
  isSectionDirty: (sectionId: string) => boolean;
  markSectionDirty: (sectionId: string) => void;
  setSelectedLocationId: (locationId: string) => void;
  setValue: (document: ComposerDocument) => void;
  sharedDynamicFieldProps?: SharedDynamicFieldsProps;
  subjectDynamicFields?: DynamicFieldAttributes[];
  templateTypeSlug: TemplateType_Slug;
  textDynamicFields?: DynamicFieldAttributes[];
  trackingIdPrefix: string;
  validate: () => Promise<ValidatorErrorWithIndex[]>;
  validateSection: (sectionId: string) => Promise<ValidatorErrorWithIndex[]>;
  validationErrors: ValidatorErrorWithId[];
  flushSectionValidation: (sectionId: string) => Promise<ValidatorErrorWithIndex[]>;
};

const SECTION_VALIDATION_IDLE_DELAY = 800;

export const [ContentComposerContext, useContentComposer] = contextFactory<ContentComposerContextState>(
  'useContentComposer must be used within a ContentComposer'
);

type ContentComposerProviderProps = {
  children: ReactNode;
  composerMode: ComposerMode;
  enableValidation?: boolean;
  form?: Form;
  initialValue?: ComposerDocument;
  locationIds: string[];
  parts: ComposerParts;
  initialSelectedLocationId?: string;
  setForm?: (form: Form) => void;
  sharedDynamicFieldProps?: SharedDynamicFieldsProps;
  subjectDynamicFields?: DynamicFieldAttributes[];
  templateTypeSlug: TemplateType_Slug;
  textDynamicFields?: DynamicFieldAttributes[];
  trackingIdPrefix: string;
};

export const ContentComposerProvider = ({
  children,
  composerMode,
  enableValidation = false,
  form,
  initialValue,
  locationIds,
  parts,
  initialSelectedLocationId,
  setForm,
  sharedDynamicFieldProps,
  subjectDynamicFields,
  templateTypeSlug,
  textDynamicFields,
  trackingIdPrefix,
}: ContentComposerProviderProps) => {
  const [selectedLocationId, setSelectedLocationId] = useState(initialSelectedLocationId || locationIds[0] || '');
  const { selectedOrgId: orgId } = useAppScopeStore();
  const { data: brandSettings } = useListBrandSettings({
    orgId,
    locationIds: enableValidation ? locationIds : [],
  });

  const { getValue, isInitialized, setValue } = useStateManagement(initialValue, parts, composerMode);
  const {
    HtmlRenderer,
    htmlRendererProps,
    startRender: renderHtml,
    cancelRender: cancelRenderHtml,
  } = useHtmlRenderer();
  const { activePanel, setActivePanel } = useActivePanel();
  const insertSection = useInsertSection();
  const promptProps = useFormField({ type: 'text' });
  const { clearSectionValidation, hasValidated, isValidating, validate, validateSection, validationErrors } =
    useValidation({
      enableValidation,
      getValue,
      isInitialized,
      locationIds,
      parts,
      selectedLocationId,
      brandSettings,
    });

  const dirtySectionIds = useRef(new Set<string>());
  const sectionValidationTimers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  const isSectionDirty = useCallback((sectionId: string) => dirtySectionIds.current.has(sectionId), []);

  const cancelScheduledSectionValidation = useCallback((sectionId: string) => {
    const timer = sectionValidationTimers.current.get(sectionId);
    if (!timer) return;
    clearTimeout(timer);
    sectionValidationTimers.current.delete(sectionId);
  }, []);

  const flushSectionValidation = useCallback(
    async (sectionId: string) => {
      if (!enableValidation || !sectionId) return [];

      cancelScheduledSectionValidation(sectionId);
      if (!dirtySectionIds.current.has(sectionId)) return [];

      dirtySectionIds.current.delete(sectionId);
      return validateSection(sectionId);
    },
    [cancelScheduledSectionValidation, enableValidation, validateSection]
  );

  const scheduleSectionValidation = useCallback(
    (sectionId: string) => {
      if (!enableValidation || !sectionId) return;

      cancelScheduledSectionValidation(sectionId);
      const timer = setTimeout(() => {
        void flushSectionValidation(sectionId);
      }, SECTION_VALIDATION_IDLE_DELAY);

      sectionValidationTimers.current.set(sectionId, timer);
    },
    [cancelScheduledSectionValidation, enableValidation, flushSectionValidation]
  );

  const markSectionDirty = useCallback(
    (sectionId: string) => {
      if (!enableValidation || !sectionId) return;

      dirtySectionIds.current.add(sectionId);
      scheduleSectionValidation(sectionId);
    },
    [enableValidation, scheduleSectionValidation]
  );

  const clearSectionValidationState = useCallback(
    (sectionId: string) => {
      if (!sectionId) return;

      cancelScheduledSectionValidation(sectionId);
      dirtySectionIds.current.delete(sectionId);
      clearSectionValidation(sectionId);
    },
    [cancelScheduledSectionValidation, clearSectionValidation]
  );

  useEffect(() => {
    const timers = sectionValidationTimers.current;

    return () => {
      timers.forEach((timer) => {
        clearTimeout(timer);
      });
      timers.clear();
    };
  }, []);

  return (
    <ContentComposerContext.Provider
      value={{
        activePanel,
        cancelRenderHtml,
        composerMode,
        form,
        getValue,
        hasValidated,
        insertSection,
        isValidating,
        locationIds,
        clearSectionValidation: clearSectionValidationState,
        flushSectionValidation,
        isSectionDirty,
        markSectionDirty,
        parts,
        promptProps,
        renderHtml,
        scheduleSectionValidation,
        selectedLocationId,
        setActivePanel,
        setForm,
        setSelectedLocationId,
        setValue,
        sharedDynamicFieldProps,
        subjectDynamicFields,
        templateTypeSlug,
        textDynamicFields,
        trackingIdPrefix,
        validate,
        validateSection,
        validationErrors,
      }}
    >
      {children}
      <HtmlRenderer {...htmlRendererProps} />
    </ContentComposerContext.Provider>
  );
};
